import { getGroups } from '../../../../../store/models/selectors';
import { TParticipant, TRecording, TUserGroup } from '../../../../../store/models/types';
import { createSelector } from 'reselect';
import { IRecordingsLang } from '../components/types';
import { ELanguageStatus, EUserGroupType } from '../../../../../store/general/types';
import { getAuthUser, getAuthUserLangCode } from '../../../../../store/general/selectors';
import { EMPTY_ARRAY } from '../../../../../../common/types';
import { IState } from '../../../../../store/types';

interface IRecordingsLangMap {
	[langCode: string]: IRecordingsLang;
}

export const getRecordingsLangMapSelector = createSelector(
	getGroups,
	getAuthUserLangCode,
	(state: IState) => getAuthUser(state)?.id || 0,
	(
		groups: TUserGroup[],
		langCode: string,
		myId: number,
	): IRecordingsLang[] => {
		const recordingsMap: IRecordingsLangMap = {};
		groups.forEach(g => {
			if (
				g.type !== EUserGroupType.CONFERENCE ||
				!g.audios || !g.audios.length
			) return;

			const lang = g.languages.find(l => l.status === ELanguageStatus.TO_LEARN);
			if (lang) {
				let recordingsLang = recordingsMap[lang.code];
				if (!recordingsLang) {
					recordingsLang = {
						id: g.id,
						langCode: lang.code,
						langName: lang.name,
						audios: [],
					}
					recordingsMap[lang.code] = recordingsLang;
				}
				const audios = g.audios.map(a => {
					const recording = a as TRecording;
					return {
						...recording,
						participants: recording.participants?.filter(p => p.id !== myId) || [],
					};
				}) as TRecording[];
				recordingsLang.audios = [
					...recordingsLang.audios,
					...audios,
				].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
			}
		})
		// the user's own language first, then most recordings, the most recent recording among equals
		// (audios are sorted newest first)
		const recordings = Object.values(recordingsMap).sort((a, b) =>
			Number(b.langCode === langCode) - Number(a.langCode === langCode)
			|| b.audios.length - a.audios.length
			|| (b.audios[0]?.createdAt || '').localeCompare(a.audios[0]?.createdAt || '')
		);
		return recordings.length ? recordings : EMPTY_ARRAY;
	}
);

// the current user as a recording participant (they're filtered out of each recording's list above)
export const getMeParticipantSelector = createSelector(
	(state: IState) => getAuthUser(state)?.id,
	(state: IState) => getAuthUser(state)?.name,
	(state: IState) => getAuthUser(state)?.picture,
	(id, name, picture): TParticipant | null => id ? { id, name: name || '', picture } : null,
);
